import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Actividades";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "—";

  const fecha = new Date(fechaStr);

  if (Number.isNaN(fecha.getTime())) return "—";

  const y = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, "0");
  const d = String(fecha.getDate()).padStart(2, "0");

  return `${d}/${m}/${y}`;
};

const ListarActividad = ({ refresh }) => {
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();
  const { id } = useParams();

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloActividades = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloActividades = useMemo(() => getModuloActividades(), []);

  const puedeCrear =
    moduloActividades?.crear === true ||
    moduloActividades?.puedeCrear === true ||
    moduloActividades?.create === true;

  const puedeActualizar =
    moduloActividades?.actualizar === true ||
    moduloActividades?.editar === true ||
    moduloActividades?.puedeActualizar === true ||
    moduloActividades?.update === true;

  const puedeEliminar =
    moduloActividades?.eliminar === true ||
    moduloActividades?.puedeEliminar === true ||
    moduloActividades?.delete === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login");
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const deleteActividad = useCallback(
    async (actividadId) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Acceso denegado",
          "No tienes permisos para eliminar actividades.",
          "warning"
        );
        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        setDeletingId(actividadId);

        const { data } = await axios.delete(
          `/api/actividad/delete/${actividadId}`,
          authConfig()
        );

        setActividades((prev) =>
          prev.filter((actividad) => actividad._id !== actividadId)
        );

        await Swal.fire({
          icon: "success",
          title: data?.message || "Actividad eliminada",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar la Actividad:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire("Error", "No se pudo eliminar la actividad.", "error");
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  const getData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/actividad/listbyid/${id}`,
        authConfig()
      );

      const lista = Array.isArray(data) ? data : data?.actividades || [];

      const formateadas = [...lista]
        .map((actividad) => ({
          ...actividad,
          fecha: formatearFecha(actividad.fecha),
        }))
        .reverse();

      setActividades(formateadas);
    } catch (error) {
      console.error("Error al cargar las actividades:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudo cargar la lista de actividades.", "error");
      setActividades([]);
    } finally {
      setLoading(false);
    }
  }, [id, manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  const actividadesConFunciones = useMemo(() => {
    return actividades.map((actividad) => ({
      ...actividad,
      funciones: (
        <div className="d-flex gap-2 flex-nowrap justify-content-end align-items-center">
          <button
            className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34 }}
            onClick={() => navigate(`/asistencia/${actividad._id}`)}
            title="Tomar asistencia"
            type="button"
            disabled={loading || deletingId === actividad._id}
          >
            <i className="fa-solid fa-list-check"></i>
          </button>

          {puedeActualizar && (
            <button
              className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34 }}
              onClick={() => navigate(`/actividad/editar/${actividad._id}`)}
              title="Editar actividad"
              type="button"
              disabled={loading || deletingId === actividad._id}
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          )}

          {puedeEliminar && (
            <button
              className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34 }}
              onClick={() => deleteActividad(actividad._id)}
              title="Eliminar actividad"
              type="button"
              disabled={loading || deletingId === actividad._id}
            >
              {deletingId === actividad._id ? (
                <i className="fa-solid fa-spinner fa-spin text-white"></i>
              ) : (
                <i className="fa-solid fa-trash"></i>
              )}
            </button>
          )}
        </div>
      ),
    }));
  }, [
    actividades,
    deleteActividad,
    deletingId,
    loading,
    navigate,
    puedeActualizar,
    puedeEliminar,
  ]);

  const columns = useMemo(
    () => [
      { Header: "Nombre", accessor: "nombreActividad" },
      { Header: "Descripción", accessor: "descripcion" },
      { Header: "Fecha", accessor: "fecha" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Lista de Actividades
                </h5>

                <small className="text-secondary">
                  Gestión de actividades, asistencia, edición y eliminación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate(`/actividad/nuevo/${id}`)}
                    disabled={loading}
                    title="Registrar actividad"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando actividades…</div>
              </div>
            ) : actividadesConFunciones.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={actividadesConFunciones} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🗂️</div>

                <div className="fw-semibold">
                  No hay actividades registradas
                </div>

                <div className="small mb-3">
                  No se encontraron actividades para esta matrícula.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate(`/actividad/nuevo/${id}`)}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear actividad
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarActividad;